import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { ChartBar, Eye, TrendUp, Bell, Clock, Buildings, Users, CurrencyDollar, Bookmark } from "@phosphor-icons/react";

const fmtAED = (n) => {
  if (!n) return "—";
  if (n >= 1_000_000_000) return `AED ${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `AED ${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `AED ${(n / 1_000).toFixed(0)}K`;
  return `AED ${Math.round(n).toLocaleString()}`;
};

const fmtHours = (h) => {
  if (h === null || h === undefined) return "—";
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 24) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} d`;
};

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-10 text-gray-500">Loading…</div>;
  const {
    totals, top_listings, lead_stages, communities, daily_leads,
    agent_leaderboard, top_journal, projects_status, lead_sources,
  } = data;
  const maxDaily = Math.max(1, ...(daily_leads || []).map((d) => d.count));
  const maxCommCount = Math.max(1, ...(communities || []).map((c) => c.count));
  const maxSource = Math.max(1, ...(lead_sources || []).map((s) => s.count));
  const totalProjects = (projects_status?.draft || 0) + (projects_status?.review || 0) + (projects_status?.published || 0);

  return (
    <div className="p-10" data-testid="admin-analytics">
      <div className="mb-8">
        <div className="text-xs tracking-[0.24em] uppercase text-gray-500 mb-1">Reporting</div>
        <h1 className="font-serif text-4xl">Analytics</h1>
        <p className="text-sm text-gray-500 mt-2">Live snapshot across mandates, agents, leads and content — refreshed on every load.</p>
      </div>

      {/* Top KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <Card icon={TrendUp} label="Live mandates" value={totals.published_listings} />
        <Card icon={Eye} label="Pending review" value={totals.pending_listings} tone={totals.pending_listings > 0 ? "amber" : "default"} />
        <Card icon={ChartBar} label="Total leads" value={totals.total_leads} />
        <Card icon={Bell} label="Fresh 24 h" value={totals.fresh_leads_24h} tone={totals.fresh_leads_24h > 0 ? "green" : "default"} />
        <Card icon={Clock} label="Median reply" value={fmtHours(totals.median_response_hours)} tone={totals.median_response_hours && totals.median_response_hours > 24 ? "amber" : "default"} />
        <Card icon={Bell} label="Unread alerts" value={totals.unread_notifications} />
      </div>

      {/* Secondary KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
        <Card icon={Buildings} label="Off-plan live" value={totals.published_projects} />
        <Card icon={Bookmark} label="Off-plan review" value={totals.review_projects} tone={totals.review_projects > 0 ? "amber" : "default"} />
        <Card icon={Users} label="Active agents" value={totals.total_agents} />
        <Card icon={Buildings} label="Communities" value={totals.total_communities} />
        <Card icon={CurrencyDollar} label="Inventory value" value={fmtAED(totals.total_inventory_value)} />
        <Card icon={CurrencyDollar} label="Median price" value={fmtAED(totals.median_sale_price)} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Daily leads */}
        <div className="border border-[#E5E7EB] bg-white p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-2xl">Leads · last 14 days</h2>
            <span className="text-xs text-gray-500">{daily_leads.reduce((s, d) => s + d.count, 0)} total · {totals.weekly_leads} this week</span>
          </div>
          <div className="flex items-end gap-1 h-40" data-testid="analytics-daily-chart">
            {daily_leads.length === 0 && <div className="text-sm text-gray-400">No lead activity yet.</div>}
            {daily_leads.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center justify-end">
                <div className="w-full bg-[#001588] hover:bg-[#29338e] transition-colors" style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: "2px" }} title={`${d.date}: ${d.count} leads`} />
                <div className="text-[9px] text-gray-400 mt-1 rotate-45 origin-left">{d.date.slice(5)}</div>
              </div>
            ))}
          </div>
        </div>
        {/* Lead stages */}
        <div className="border border-[#E5E7EB] bg-white p-6">
          <h2 className="font-serif text-2xl mb-6">Lead pipeline</h2>
          <div className="space-y-3">
            {["new","contacted","qualified","viewing","offer","closed"].map((s) => {
              const c = lead_stages[s] || 0;
              const total = Object.values(lead_stages).reduce((a, b) => a + b, 0) || 1;
              return (
                <div key={s} data-testid={`analytics-stage-${s}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="capitalize">{s}</span>
                    <span className="text-gray-500">{c} · {Math.round((c / total) * 100)}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-100">
                    <div className="h-full bg-[#001588]" style={{ width: `${(c / total) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Agent leaderboard */}
        <div className="border border-[#E5E7EB] bg-white p-6" data-testid="analytics-agent-leaderboard">
          <h2 className="font-serif text-2xl mb-6">Agent leaderboard</h2>
          {agent_leaderboard.length === 0 && <div className="text-sm text-gray-400">No leads assigned yet.</div>}
          <div className="divide-y divide-[#E5E7EB]">
            {agent_leaderboard.map((a, idx) => (
              <div key={a.agent_id} className="flex items-center justify-between py-3" data-testid={`analytics-agent-${a.agent_id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-7 h-7 bg-[#001588] text-white text-xs font-medium flex items-center justify-center shrink-0">{idx + 1}</div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{a.name}</div>
                    <div className="text-xs text-gray-500 capitalize">{a.role} · {a.qualified_leads} qualified</div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-medium">{a.total_leads} leads</div>
                  <div className="text-xs text-gray-500">{a.close_rate}% close</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Lead sources */}
        <div className="border border-[#E5E7EB] bg-white p-6" data-testid="analytics-lead-sources">
          <h2 className="font-serif text-2xl mb-6">Lead sources</h2>
          {(!lead_sources || lead_sources.length === 0) && <div className="text-sm text-gray-400">No leads yet.</div>}
          <div className="space-y-3">
            {lead_sources.map((s) => (
              <div key={s.source} data-testid={`analytics-source-${s.source}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="capitalize">{s.source}</span>
                  <span className="text-gray-500">{s.count}</span>
                </div>
                <div className="h-1.5 bg-gray-100">
                  <div className="h-full bg-[#001588]" style={{ width: `${(s.count / maxSource) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Top listings */}
        <div className="border border-[#E5E7EB] bg-white p-6">
          <h2 className="font-serif text-2xl mb-6">Top viewed mandates</h2>
          <div className="divide-y divide-[#E5E7EB]">
            {top_listings.length === 0 && <div className="text-sm text-gray-400">No views yet.</div>}
            {top_listings.map((l) => (
              <Link key={l.id} to={`/properties/${l.id}`} className="flex items-center justify-between py-3 hover:bg-gray-50 -mx-2 px-2" data-testid={`analytics-top-${l.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{l.title}</div>
                  <div className="text-xs text-gray-500">{l.community} · {l.reference}</div>
                </div>
                <div className="flex items-center gap-1 text-sm text-gray-600 shrink-0">
                  <Eye size={14} /> {l.views}
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Top journal */}
        <div className="border border-[#E5E7EB] bg-white p-6" data-testid="analytics-top-journal">
          <h2 className="font-serif text-2xl mb-6">Top journal posts</h2>
          <div className="divide-y divide-[#E5E7EB]">
            {(!top_journal || top_journal.length === 0) && <div className="text-sm text-gray-400">No published journal posts yet.</div>}
            {top_journal && top_journal.map((j) => (
              <Link key={j.slug} to={`/journal/${j.slug}`} className="flex items-center justify-between py-3 hover:bg-gray-50 -mx-2 px-2" data-testid={`analytics-journal-${j.slug}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{j.title}</div>
                  <div className="text-xs text-gray-500">{j.published_at ? j.published_at.slice(0, 10) : "unpublished"}</div>
                </div>
                <div className="flex items-center gap-1 text-sm text-gray-600 shrink-0">
                  <Eye size={14} /> {j.views || 0}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Community distribution */}
        <div className="border border-[#E5E7EB] bg-white p-6">
          <h2 className="font-serif text-2xl mb-6">Inventory by community</h2>
          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
            {communities.slice(0, 12).map((c) => (
              <div key={c.community} data-testid={`analytics-community-${c.community?.replace(/\s+/g,'-')}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="truncate pr-2">{c.community}</span>
                  <span className="text-gray-500 shrink-0 text-xs">{c.count} · avg {fmtAED(c.avg_price)}</span>
                </div>
                <div className="h-1.5 bg-gray-100">
                  <div className="h-full bg-[#001588]" style={{ width: `${(c.count / maxCommCount) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Off-plan status */}
        <div className="border border-[#E5E7EB] bg-white p-6" data-testid="analytics-project-status">
          <h2 className="font-serif text-2xl mb-6">Off-plan projects status</h2>
          {totalProjects === 0 && <div className="text-sm text-gray-400">No off-plan projects yet.</div>}
          <div className="space-y-3">
            {["published", "review", "draft"].map((s) => {
              const c = projects_status?.[s] || 0;
              return (
                <div key={s} data-testid={`analytics-project-status-${s}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="capitalize">{s}</span>
                    <span className="text-gray-500">{c} · {totalProjects ? Math.round((c / totalProjects) * 100) : 0}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-100">
                    <div className={`h-full ${s === "review" ? "bg-amber-500" : s === "draft" ? "bg-gray-400" : "bg-[#001588]"}`} style={{ width: `${totalProjects ? (c / totalProjects) * 100 : 0}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value, tone = "default" }) {
  const toneCls = tone === "green"
    ? "border-emerald-300 bg-emerald-50"
    : tone === "amber"
      ? "border-amber-300 bg-amber-50"
      : "border-[#E5E7EB] bg-white";
  return (
    <div className={`border ${toneCls} p-5`} data-testid={`analytics-card-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center justify-between mb-3">
        <Icon size={20} className="text-gray-400" weight="thin" />
      </div>
      <div className="font-serif text-2xl mb-1 truncate">{value}</div>
      <div className="text-[10px] tracking-wider uppercase text-gray-500">{label}</div>
    </div>
  );
}
